import { sendTransaction } from "@wagmi/core";
import { isAddress } from "viem";
import { getWagmiConfig } from "@/lib/wallet";

export type SplitLeg = {
  label: string;
  to: `0x${string}`;
  value: bigint;
};

/**
 * On-chain fee split. Sends one native-token transaction per non-zero leg,
 * in order. Returns the array of tx hashes; the LAST hash is the "main"
 * settlement transfer (seller / creator / contract) — the caller records that
 * as the primary tx_hash for the mint/buy activity.
 *
 * Zero-value legs are skipped. If any leg reverts / user rejects, the whole
 * sequence aborts and the error bubbles up — earlier legs already sent stay
 * on-chain (this is inherent to multi-tx wallet-side splitting; a single
 * atomic split requires a Splitter/Marketplace contract).
 */
export async function executeFeeSplit(legs: SplitLeg[]): Promise<{ hashes: string[]; mainHash: string }> {
  const active = legs.filter((l) => l.value > 0n && isAddress(l.to));
  if (active.length === 0) throw new Error("Nothing to send");
  const config = getWagmiConfig();
  const hashes: string[] = [];
  for (const leg of active) {
    const hash = await sendTransaction(config, { to: leg.to, value: leg.value });
    hashes.push(hash);
  }
  return { hashes, mainHash: hashes[hashes.length - 1] };
}

/**
 * Compute mint fee legs: platform fee -> feeRecipient, remainder -> payTo.
 * When feeRecipient is missing/invalid, platform fee is folded into remainder.
 */
export function buildMintLegs(opts: {
  totalWei: bigint;
  mintFeeBps: number;
  feeRecipient?: string | null;
  payTo: `0x${string}`; // contract if deployed, else creator
}): SplitLeg[] {
  const { totalWei, mintFeeBps, feeRecipient, payTo } = opts;
  const legs: SplitLeg[] = [];
  let remainder = totalWei;
  if (feeRecipient && isAddress(feeRecipient) && mintFeeBps > 0 && totalWei > 0n) {
    const fee = (totalWei * BigInt(mintFeeBps)) / 10000n;
    if (fee > 0n) {
      legs.push({ label: "Platform mint fee", to: feeRecipient as `0x${string}`, value: fee });
      remainder -= fee;
    }
  }
  if (remainder > 0n) legs.push({ label: "Mint payment", to: payTo, value: remainder });
  return legs;
}

/**
 * Compute buy/trade fee legs: platform fee -> feeRecipient, creator royalty
 * -> creator, remainder -> seller.
 */
export function buildBuyLegs(opts: {
  totalWei: bigint;
  listFeeBps: number;
  creatorFeeBps: number;
  feeRecipient?: string | null;
  creator: string;
  seller: `0x${string}`;
}): SplitLeg[] {
  const { totalWei, listFeeBps, creatorFeeBps, feeRecipient, creator, seller } = opts;
  const legs: SplitLeg[] = [];
  let remainder = totalWei;
  if (feeRecipient && isAddress(feeRecipient) && listFeeBps > 0 && totalWei > 0n) {
    const fee = (totalWei * BigInt(listFeeBps)) / 10000n;
    if (fee > 0n) {
      legs.push({ label: "Platform trade fee", to: feeRecipient as `0x${string}`, value: fee });
      remainder -= fee;
    }
  }
  if (creator && isAddress(creator) && creator.toLowerCase() !== seller.toLowerCase() && creatorFeeBps > 0 && totalWei > 0n) {
    const royalty = (totalWei * BigInt(creatorFeeBps)) / 10000n;
    if (royalty > 0n) {
      legs.push({ label: "Creator royalty", to: creator as `0x${string}`, value: royalty });
      remainder -= royalty;
    }
  }
  if (remainder > 0n) legs.push({ label: "Seller payment", to: seller, value: remainder });
  return legs;
}
